export const COMPLETION_THRESHOLD_SECONDS = 10;

const STORAGE_PREFIX = "cfdg:cms-playlist:vimeo-progress:v1";

function normalizeIdentifier(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === "string" && value.trim() !== "") {
    return value.trim();
  }
  return null;
}

function resolveStorage(storage) {
  if (storage !== undefined) {
    return storage;
  }

  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch (error) {
    return null;
  }
}

export function buildPlaybackProgressKey({memberId, playlistId, videoId, vimeoId} = {}) {
  const identifiers = [memberId, playlistId, videoId, vimeoId].map(normalizeIdentifier);
  if (identifiers.some(identifier => identifier === null)) {
    return null;
  }

  try {
    return [STORAGE_PREFIX, ...identifiers.map(encodeURIComponent)].join(":");
  } catch (error) {
    return null;
  }
}

export function normalizePlaybackProgress(progress) {
  if (!progress || typeof progress !== "object") {
    return null;
  }

  const {seconds, duration} = progress;
  if (
    typeof seconds !== "number" ||
    !Number.isFinite(seconds) ||
    seconds < 0 ||
    typeof duration !== "number" ||
    !Number.isFinite(duration) ||
    duration <= 0
  ) {
    return null;
  }

  return {seconds, duration};
}

export function isEffectivelyComplete(progress) {
  const normalized = normalizePlaybackProgress(progress);
  return normalized !== null && normalized.duration - normalized.seconds <= COMPLETION_THRESHOLD_SECONDS;
}

export function clearPlaybackProgress(identity, storage) {
  const key = buildPlaybackProgressKey(identity);
  const target = resolveStorage(storage);
  if (key === null || !target) {
    return false;
  }

  try {
    if (typeof target.removeItem !== "function") {
      return false;
    }
    target.removeItem(key);
    return true;
  } catch (error) {
    return false;
  }
}

export function readPlaybackProgress(identity, storage) {
  const key = buildPlaybackProgressKey(identity);
  const target = resolveStorage(storage);
  if (key === null || !target) {
    return null;
  }

  try {
    if (typeof target.getItem !== "function") {
      return null;
    }
    const stored = target.getItem(key);
    if (stored === null) {
      return null;
    }

    const progress = normalizePlaybackProgress(JSON.parse(stored));
    if (progress === null) {
      return null;
    }
    if (isEffectivelyComplete(progress)) {
      clearPlaybackProgress(identity, target);
      return null;
    }
    return progress;
  } catch (error) {
    return null;
  }
}

export function savePlaybackProgress(identity, progress, storage) {
  const key = buildPlaybackProgressKey(identity);
  const normalized = normalizePlaybackProgress(progress);
  const target = resolveStorage(storage);
  if (key === null || normalized === null || !target) {
    return false;
  }

  if (isEffectivelyComplete(normalized)) {
    return clearPlaybackProgress(identity, target);
  }

  try {
    if (typeof target.setItem !== "function") {
      return false;
    }
    target.setItem(key, JSON.stringify(normalized));
    return true;
  } catch (error) {
    return false;
  }
}
